import React, {ReactNode} from 'react'
import {useAuth} from './core/Auth'

export const RoleNames = {
  Observer: 'Observer',
  User: 'User',
  Admin: 'Admin',
} as const

export type RoleName = typeof RoleNames[keyof typeof RoleNames]

export const Permissions = {
  ViewData: 'Data.View',
  CreateOrUpdateData: 'Data.CreateOrUpdate',
  ImportData: 'Data.Import',
  ExportData: 'Data.Export',
  DeleteOwnDraft: 'Data.DeleteOwnDraft',
  DeleteAnyData: 'Data.DeleteAny',
  ManageParameters: 'Parameters.Manage',
  ManageUsers: 'Users.Manage',
  ManageScopes: 'Scopes.Manage',
  ManageSystem: 'System.Manage',
  ViewAuditLogs: 'Audit.View',
  AssignTasks: 'Tasks.Assign',
  ViewWorkforceProfile: 'WorkforceProfile.View',
  ViewWorkforceProfileDetails: 'WorkforceProfile.ViewDetails',
  ExportWorkforceProfile: 'WorkforceProfile.Export',
  ViewWorkforceProfileSensitiveData: 'WorkforceProfile.ViewSensitiveData',
  ManageWorkforceProfileLookups: 'WorkforceProfile.ManageLookups',
  ViewEmployeeMovements: 'EmployeeMovements.View',
  ViewEmployeeMovementsDetails: 'EmployeeMovements.ViewDetails',
  ExportEmployeeMovements: 'EmployeeMovements.Export',
  ViewEmployeeMovementsSensitiveData: 'EmployeeMovements.ViewSensitiveData',
  ManageEmployeeMovements: 'EmployeeMovements.Manage',
  ViewManagerCompensation: 'ManagerCompensationView.View',
  ViewManagerCompensationDetails: 'ManagerCompensationView.ViewDetails',
  ViewManagerCompensationSalary: 'ManagerCompensationView.ViewSalary',
  ViewManagerCompensationCr: 'ManagerCompensationView.ViewCR',
  ViewManagerCompensationPir: 'ManagerCompensationView.ViewPIR',
  ExportManagerCompensation: 'ManagerCompensationView.Export',
  ShareManagerCompensation: 'ManagerCompensationView.Share',
  ViewManagerCompensationHistorical: 'ManagerCompensationView.ViewHistorical',
  ViewManagerCompensationSensitiveData: 'ManagerCompensationView.ViewSensitiveData',
  ViewPerformanceAlignment: 'PerformanceAlignment.View',
  ViewPerformanceAlignmentDetails: 'PerformanceAlignment.ViewDetails',
  ViewPerformanceAlignmentPerformance: 'PerformanceAlignment.ViewPerformance',
  ViewPerformanceAlignmentSalary: 'PerformanceAlignment.ViewSalary',
  ViewPerformanceAlignmentCr: 'PerformanceAlignment.ViewCR',
  ViewPerformanceAlignmentPir: 'PerformanceAlignment.ViewPIR',
  ViewPerformanceAlignmentScenarioCost: 'PerformanceAlignment.ViewScenarioCost',
  ExportPerformanceAlignment: 'PerformanceAlignment.Export',
  ManagePerformanceData: 'PerformanceAlignment.ManagePerformanceData',
  ImportPerformanceData: 'PerformanceAlignment.ImportPerformanceData',
  ManagePerformanceActions: 'PerformanceAlignment.ManageActions',
  AcceptPerformanceExceptions: 'PerformanceAlignment.AcceptExceptions',
  ViewPerformanceAlignmentSensitiveData: 'PerformanceAlignment.ViewSensitiveData',
  ViewReportSettings: 'ReportSettings.View',
  ApplyTemporaryReportSettings: 'ReportSettings.ApplyTemporary',
  SavePersonalReportSettings: 'ReportSettings.SavePersonal',
  PublishReportSettingsDefault: 'ReportSettings.PublishDefault',
  ViewReportSettingsHistory: 'ReportSettings.ViewHistory',
  ManageScopedReportSettings: 'ReportSettings.ManageScopedDefaults',
  PreviewReportSettingsImpact: 'ReportSettings.PreviewImpact',
  ViewLogisticsDelegation: 'LogisticsDelegation.View',
  CalculateLogisticsDelegation: 'LogisticsDelegation.Calculate',
  ImportLogisticsDelegation: 'LogisticsDelegation.Import',
  ExportLogisticsDelegation: 'LogisticsDelegation.Export',
  ManageLogisticsDelegationPeriods: 'LogisticsDelegation.ManagePeriods',
} as const

export type Permission = typeof Permissions[keyof typeof Permissions]

const rolePermissions: Record<RoleName, ReadonlySet<Permission>> = {
  [RoleNames.Observer]: new Set<Permission>([
    Permissions.ViewData,
    Permissions.ExportData,
    Permissions.ViewWorkforceProfile,
    Permissions.ViewEmployeeMovements,
    Permissions.ViewPerformanceAlignment,
    Permissions.ViewReportSettings,
  ]),
  [RoleNames.User]: new Set<Permission>([
    Permissions.ViewData,
    Permissions.CreateOrUpdateData,
    Permissions.ImportData,
    Permissions.ExportData,
    Permissions.DeleteOwnDraft,
    Permissions.AssignTasks,
    Permissions.ViewWorkforceProfile,
    Permissions.ViewWorkforceProfileDetails,
    Permissions.ExportWorkforceProfile,
    Permissions.ViewEmployeeMovements,
    Permissions.ViewEmployeeMovementsDetails,
    Permissions.ExportEmployeeMovements,
    Permissions.ViewManagerCompensation,
    Permissions.ViewManagerCompensationDetails,
    Permissions.ViewManagerCompensationSalary,
    Permissions.ViewManagerCompensationCr,
    Permissions.ViewManagerCompensationPir,
    Permissions.ExportManagerCompensation,
    Permissions.ShareManagerCompensation,
    Permissions.ViewManagerCompensationHistorical,
    Permissions.ViewPerformanceAlignment,
    Permissions.ViewPerformanceAlignmentDetails,
    Permissions.ViewPerformanceAlignmentPerformance,
    Permissions.ViewPerformanceAlignmentSalary,
    Permissions.ViewPerformanceAlignmentCr,
    Permissions.ViewPerformanceAlignmentPir,
    Permissions.ViewPerformanceAlignmentScenarioCost,
    Permissions.ExportPerformanceAlignment,
    Permissions.ManagePerformanceActions,
    Permissions.ViewReportSettings,
    Permissions.ApplyTemporaryReportSettings,
    Permissions.SavePersonalReportSettings,
    Permissions.PreviewReportSettingsImpact,
    Permissions.ViewLogisticsDelegation,
    Permissions.CalculateLogisticsDelegation,
    Permissions.ImportLogisticsDelegation,
    Permissions.ExportLogisticsDelegation,
  ]),
  [RoleNames.Admin]: new Set<Permission>(Object.values(Permissions)),
}

export const normalizeRole = (role?: string | null): RoleName | undefined => {
  switch (role?.trim().toUpperCase()) {
    case 'OBSERVER':
      return RoleNames.Observer
    case 'USER':
      return RoleNames.User
    case 'ADMIN':
      return RoleNames.Admin
    default:
      return undefined
  }
}

export const hasPermission = (role: string | null | undefined, permission: Permission): boolean => {
  const canonicalRole = normalizeRole(role)
  return canonicalRole ? rolePermissions[canonicalRole].has(permission) : false
}

export const usePermission = (permission: Permission): boolean => {
  const {auth} = useAuth()
  return hasPermission(auth?.role, permission)
}

export const PermissionDenied = () => (
  <section className='cbbee-panel'>
    <div className='cbbee-empty-state' role='alert'>
      <i className='bi bi-shield-lock me-2' aria-hidden='true'></i>
      Bu işlem için yetkiniz bulunmuyor
    </div>
  </section>
)

export const RequirePermission = ({
  permission,
  children,
}: {
  permission: Permission
  children: ReactNode
}) => (usePermission(permission) ? <>{children}</> : <PermissionDenied />)

export const PermissionGate = ({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission
  children: ReactNode
  fallback?: ReactNode
}) => (usePermission(permission) ? <>{children}</> : <>{fallback}</>)
